<template>
    <div class="font-roboto table-responsive px-2.5 py-1">
        <table v-if="tableData && tableData.length > 0" class="w-full border-separate border-spacing-y-1">
            <thead>
                <tr class="rounded-lg border border-white shadow-[0_0_10px_0_#00000026]">
                    <th v-for="(header, index) in tableHeaders" :key="header.title"
                        class="p-2 font-roboto font-medium text-[12px] largeDesktop:text-[14px] cursor-pointer" :class="{
                            'rounded-l-lg': index === 0,
                            'rounded-r-lg': index === tableHeaders.length - 1,
                            '!bg-[#BA90C6] text-white': true,
                            '!min-w-[100px] !max-w-[120px]': header.data_value === 'id',
                            '!min-w-[150px]': header.data_value === 'full_name',
                            '!min-w-[160px]': header.data_value === 'created_at' || header.data_value === 'Source',
                            '!min-w-[120px]': header.data_value === 'Source' || header.data_value === 'selected_payment',
                            '!min-w-[170px] !max-w-[200px] cursor-default': header.data_value === 'status_text',
                        }" @click="sortClicked(header)">
                        <span class="flex items-center justify-between font-roboto font-medium">
                            {{ header.title }}
                            <span v-if="header.sort_applicable == '1'" class="max-h-5">
                                <button v-if="header.sort_direction == ''" class="p-0 border-0 focus:outline-none">
                                    <!-- neutral ⇅ until sorted, as in Figma -->
                                    <img src="../../assets/images/sorting-icon.png" alt="Sort"
                                        class="w-4 h-4 object-contain">
                                </button>
                                <button v-else-if="header.sort_direction == 'ASC'"
                                    class="p-0 border-0 focus:outline-none">
                                    <img src="../../assets/images/asc.svg" alt="Sorting Ascending Arrow"
                                        class="w-4 h-4 object-contain">
                                </button>
                                <button v-else-if="header.sort_direction == 'DESC'"
                                    class="p-0 border-0 focus:outline-none">
                                    <img src="../../assets/images/desc.svg" alt="Sorting Descending Arrow"
                                        class="w-4 h-4 object-contain">
                                </button>
                            </span>
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody class="bg-white text-slate-800 table-row-group space-y-2 w-full">
                <tr v-for="(item, rowIndex) in tableData" :key="rowIndex"
                    class="mt-1 border-0 py-2 rounded-lg shadow-[0_0_10px_0_#00000026] cursor-pointer"
                    @click="$event.target.closest('a') || detailClicked(item.id)">
                    <td v-for="(header, colIndex) in tableHeaders" :key="header.data_value"
                        class="p-2 font-roboto text-[12px] largeDesktop:text-[14px] text-[#333333] bg-white" :class="{
                            '!text-[#7695FF]': header.data_value === 'id',
                            'rounded-l-lg': colIndex === 0,
                            'rounded-r-lg': colIndex === tableHeaders.length - 1,
                            '!min-w-[100px] !max-w-[120px]': header.data_value === 'id',
                            '!min-w-[150px]': header.data_value === 'full_name',
                            '!min-w-[160px]': header.data_value === 'created_at' || header.data_value === 'Source',
                            '!min-w-[120px]': header.data_value === 'title' || header.data_value === 'selected_payment',
                            '!min-w-[170px] !max-w-[200px]': header.data_value === 'status_text',
                        }">
                        <!-- real <a href>: opens in a new tab too; the row click covers the cell padding -->
                        <router-link :to="detailRoute(item.id)" class="block no-underline text-inherit">
                        <template
                            v-if="header.data_value === 'created_at' || header.data_value === 'installation_datetime'">
                            <span>
                                {{ isBlank(item[header.data_value]) || item[header.data_value] == "-" ? '-' :
                                    $helper.formatDateTime(item[header.data_value]) }}
                            </span>
                        </template>
                        <template v-else-if="header.data_value === 'selected_payment'">
                            {{ amount(item) }}
                        </template>
                        <template v-else-if="header.data_value === 'id'">
                            <span class="flex items-center gap-[8px]">
                                {{ show(item.id) }}
                                <img v-if="partnerLogo(item)" :src="partnerLogo(item)" :alt="item.partner_info.company_name || 'Partner'"
                                    class="h-[24px] max-w-[70px] object-contain flex-shrink-0" @error="$event.target.style.display = 'none'">
                            </span>
                        </template>
                        <template v-else-if="header.data_value === 'full_name'">
                            {{ show(item.consumer?.full_name) }}
                        </template>
                        <template v-else-if="header.data_value === 'Source'">
                            {{ isBlank(item.job_source?.title) ? '-' : item.job_source.title + ' - ' +
                                item.job_source.id }}
                        </template>
                        <template v-else-if="header.data_value === 'title'">
                            {{ show(item.job?.title) }}
                        </template>
                        <template
                            v-else-if="header.data_value === 'status_text' && isBlank(item.status?.consumer?.text)">
                            -
                        </template>
                        <template v-else-if="header.data_value === 'status_text'">
                            <span class="text-white rounded-md p-[5px] py-[2px] largeDesktop:px-2.5 largeDesktop:py-1 capitalize block w-fit"
                                :style="{ backgroundColor: getStatusClass(item.status.consumer.text) }">
                                {{ item.status.consumer.text }}
                            </span>
                        </template>
                        <template v-else>
                            {{ show(item[header.data_value]) }}
                        </template>
                        </router-link>
                    </td>
                </tr>
            </tbody>
        </table>
        <!-- the list page puts its "+ Create …" button here -->
        <EmptyState v-else :text="emptyText || undefined"><slot name="empty" /></EmptyState>
    </div>
</template>

<script>
import EmptyState from "@/components/partialComponent/EmptyState.vue";
import { isEmpty } from 'lodash';

export default {
  components: { EmptyState },
    props: ["tableHeaders", "tableData", "sortClicked", "isLoading", "emptyText"],
    methods: {
        // partner (NRG / DEPP) quote: partner_info.status with the company logo (Android IndependentQuoteListingAdapter)
        partnerLogo(item) {
            const p = item.partner_info;
            return p && [1, "1", true].includes(p.status) && p.company_logo ? p.company_logo : null;
        },
        // no amount yet: $0.00, not "-"
        amount(item) {
            return this.$helper.isEmpty(item.selected_payment?.amount_prefix) ? "$0.00" : item.selected_payment.amount_prefix;
        },
        isBlank(value) {
            return value === null || value === undefined || String(value).trim() === '';
        },
        // Empty cells show "-"
        show(value) {
            return this.isBlank(value) ? '-' : value;
        },
        detailRoute(id) {
            return { name: "QuoteDetail", params: { id: id } };
        },
        detailClicked(id) {
            this.$router.push(this.detailRoute(id));
        },
        getStatusClass(status) {
            const statusClasses = {
                'Started': '#00BCD4',
                'Installation Started': '#65B741',
                'Pending Checkout': '#429538',
                'Pending': '#F59E0B',
                'Pending Approval': '#8B5E3C',
                'Suspended': '#DC2626',
                'Payment Completed': '#65B741',
                'Completed': '#10B981',
                'Order Closed': '#686D76',
                'Scheduled': '#0284C7',
                'Cancelled': '#B91C1C',
            };
            return statusClasses[status] || '#6B7280'; // inline style needs a colour, not a class (was 'bg-gray-500' → invisible badge)
        },
    }
};
</script>
